import { useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import StatusBadge from '../components/StatusBadge';
import HealthScoreGauge from '../components/HealthScoreGauge';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import {
  ArrowLeft,
  ChevronRight,
  Phone,
  Mail,
  FileText,
  UserPlus,
  CheckCircle2,
  History,
  Wrench,
  AlertTriangle,
  Thermometer,
  Droplet,
  Wind,
  Clock,
  Activity,
  Gauge,
  Zap,
  Signal,
  CircuitBoard,
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LineChart,
  Line,
  BarChart,
  Bar,
} from 'recharts';
import { toast } from 'sonner';

function genTemp24h(setpoint, indoor) {
  return Array.from({ length: 24 }).map((_, i) => ({
    t: `${i}:00`,
    indoor: indoor + Math.sin(i / 3) * 1.2 + (i > 14 && i < 19 ? 1.5 : 0),
    setpoint,
  }));
}
function genHumidity7d(hum) {
  return Array.from({ length: 7 }).map((_, i) => ({
    d: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i],
    humidity: Math.round(hum + Math.sin(i) * 3 + (i === 5 ? 4 : 0)),
  }));
}
function genRuntime7d() {
  return Array.from({ length: 7 }).map((_, i) => ({
    d: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i],
    minutes: 220 + Math.round(Math.sin(i / 1.3) * 80 + i * 10),
  }));
}
function genHealthTrend(current) {
  return Array.from({ length: 12 }).map((_, i) => ({
    w: `W${i + 1}`,
    score: Math.max(30, Math.min(100, current - 10 + i + Math.round(Math.sin(i / 1.3) * 4))),
  }));
}
function genCycles() {
  return Array.from({ length: 12 }).map((_, i) => ({
    h: `${i * 2}:00`,
    cycles: 3 + Math.round(Math.abs(Math.sin(i / 2)) * 6),
  }));
}

const SUB_SCORES = [
  { key: 'cool', label: 'Cooling efficiency', icon: Thermometer },
  { key: 'heat', label: 'Heating efficiency', icon: Zap },
  { key: 'airflow', label: 'Airflow confidence', icon: Wind },
  { key: 'humidity', label: 'Humidity control', icon: Droplet },
  { key: 'runtime', label: 'Runtime stability', icon: Clock },
  { key: 'sensor', label: 'Sensor consistency', icon: CircuitBoard },
  { key: 'conn', label: 'Connectivity', icon: Signal },
];

export default function DeviceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { devices, customers, alerts, technicians, createTicketFromAlert } = useApp();

  const device = devices.find((d) => d.id === id);
  const customer = device ? customers.find((c) => c.id === device.customerId) : null;
  const tech = customer ? technicians.find((t) => t.id === customer.preferredTechId) : null;
  const deviceAlerts = alerts.filter((a) => a.deviceId === id);
  const mainAlert = deviceAlerts[0];

  const temps = useMemo(() => device ? genTemp24h(device.setpoint, device.indoorTemp) : [], [device]);
  const hum = useMemo(() => device ? genHumidity7d(device.humidity) : [], [device]);
  const runtime = useMemo(() => genRuntime7d(), []);
  const healthTrend = useMemo(() => device ? genHealthTrend(device.healthScore) : [], [device]);
  const cycles = useMemo(() => genCycles(), []);

  if (!device || !customer) {
    return (
      <div className="text-center py-20">
        <div className="text-slate-500">Device not found.</div>
        <Button className="mt-4" onClick={() => navigate('/devices')}>Back to Device Monitor</Button>
      </div>
    );
  }

  const baseScore = device.healthScore;
  const subs = {
    cool: Math.max(40, baseScore + 2),
    heat: Math.max(50, baseScore - 4),
    airflow: Math.max(30, baseScore - 12),
    humidity: Math.max(35, baseScore - 8),
    runtime: Math.max(40, baseScore - 6),
    sensor: Math.max(60, baseScore + 3),
    conn: device.connection === 'offline' ? 0 : 98,
  };

  const diagnostics = [
    { name: 'Short cycling detection', status: baseScore < 50 ? 'critical' : baseScore < 75 ? 'watch' : 'normal', confidence: 88, explanation: 'System cycle count per hour compared to seasonal baseline.', action: 'Inspect thermostat cycle settings and refrigerant charge.' },
    { name: 'Extended runtime', status: baseScore < 60 ? 'warning' : 'normal', confidence: 82, explanation: 'Cumulative runtime vs comparable homes on similar outdoor temperatures.', action: 'Evaluate filter, coil, and airflow path.' },
    { name: 'Failure to reach setpoint', status: baseScore < 55 ? 'critical' : baseScore < 75 ? 'watch' : 'normal', confidence: 91, explanation: 'Indoor temperature delta from setpoint over rolling 3-hour windows.', action: 'Priority diagnostic visit recommended.' },
    { name: 'Humidity drift', status: device.humidity > 60 ? 'warning' : 'normal', confidence: 76, explanation: 'Indoor RH trend vs target comfort band.', action: 'Verify dehumidification mode and drain operation.' },
    { name: 'Airflow restriction', status: baseScore < 65 ? 'warning' : 'normal', confidence: 70, explanation: 'ΔT across coil + blower amp signature analysis.', action: 'Replace filter. Inspect blower wheel.' },
    { name: 'Sensor imbalance', status: 'normal', confidence: 94, explanation: 'Remote sensor drift vs primary thermostat reading.', action: 'No action required.' },
    { name: 'Repeated high-stage usage', status: baseScore < 70 ? 'watch' : 'normal', confidence: 68, explanation: 'Stage-2 cooling engagement frequency over 24h window.', action: 'Check system sizing and charge.' },
    { name: 'Offline communication', status: device.connection === 'offline' ? 'critical' : 'normal', confidence: 99, explanation: 'Device check-in latency with Honeywell cloud.', action: 'Verify Wi-Fi, power, and re-provision device if needed.' },
    { name: 'Unusual fan runtime', status: device.equipmentStatus === 'fan' ? 'watch' : 'normal', confidence: 72, explanation: 'Blower runtime without active heating or cooling demand.', action: 'Confirm fan mode setting.' },
    { name: 'IAQ / VOC concern', status: baseScore < 70 && device.humidity > 55 ? 'watch' : 'normal', confidence: 64, explanation: 'IAQ index signals based on local trends (simulated).', action: 'Offer IAQ upgrade consultation.' },
    { name: 'Estimated CO2 concern', status: 'normal', confidence: 58, explanation: 'CO2 modeled from occupancy + ventilation schedule.', action: 'No action required.' },
  ];

  const suggested = mainAlert?.recommended || 'System operating within expected parameters. Continue standard monitoring.';

  const handleCreateTicket = () => {
    if (!mainAlert) {
      toast.info('No active alert on this device.');
      return;
    }
    const t = createTicketFromAlert(mainAlert);
    toast.success(`Service ticket ${t.id} created`);
    navigate(`/tickets/${t.id}`);
  };

  return (
    <div className="space-y-6" data-testid="device-detail-page">
      {/* Breadcrumb + header */}
      <div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <button onClick={() => navigate('/devices')} className="hover:text-slate-700 inline-flex items-center gap-1">
            <ArrowLeft className="h-3 w-3" /> Devices
          </button>
          <ChevronRight className="h-3 w-3" />
          <span className="font-mono">{device.id}</span>
        </div>
        <div className="mt-3 flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Device Profile</div>
            <h1 className="mt-1 text-3xl font-semibold text-slate-900 tracking-tight">{customer.name}</h1>
            <div className="mt-1 text-sm text-slate-500">
              {device.model} · <span className="font-mono">{device.id}</span> · {device.address}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <StatusBadge status={device.connection === 'offline' ? 'offline' : device.healthBand.toLowerCase()} />
              <StatusBadge status={device.mode}>{device.mode === 'cool' ? 'Cool' : device.mode === 'heat' ? 'Heat' : device.mode === 'auto' ? 'Auto' : 'Off'}</StatusBadge>
              <StatusBadge status={device.equipmentStatus}>{device.equipmentStatus === 'fan' ? 'Fan Only' : device.equipmentStatus === 'aux' ? 'Aux Heat' : device.equipmentStatus.charAt(0).toUpperCase() + device.equipmentStatus.slice(1)}</StatusBadge>
              {device.hasActiveAlert && (
                <span className="inline-flex items-center gap-1 text-xs text-red-600 font-medium">
                  <AlertTriangle className="h-3.5 w-3.5" /> {deviceAlerts.length} active alert{deviceAlerts.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button className="bg-[#0052CC] hover:bg-[#0043A6]" onClick={handleCreateTicket} data-testid="device-create-ticket">
              <Wrench className="h-4 w-4 mr-2" /> Create Service Ticket
            </Button>
            <Button variant="outline" data-testid="device-assign-tech"><UserPlus className="h-4 w-4 mr-2" />Assign Technician</Button>
            <Button variant="outline" data-testid="device-mark-reviewed" onClick={() => toast.success('Marked as reviewed')}><CheckCircle2 className="h-4 w-4 mr-2" />Mark Reviewed</Button>
            <Button variant="outline" data-testid="device-call-customer" onClick={() => toast.success(`Calling ${customer.name}...`)}><Phone className="h-4 w-4 mr-2" />Call</Button>
            <Button variant="outline" data-testid="device-email-customer" onClick={() => toast.success(`Email composer opened for ${customer.email}`)}><Mail className="h-4 w-4 mr-2" />Email</Button>
          </div>
        </div>
      </div>

      {/* Suggested action */}
      {mainAlert && (
        <Card className="p-5 border-slate-200 shadow-sm bg-gradient-to-r from-blue-50/70 to-white" data-testid="suggested-action-card">
          <div className="flex items-start gap-4">
            <div className="h-10 w-10 rounded-md bg-[#0052CC] text-white flex items-center justify-center shrink-0">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <div className="text-xs uppercase tracking-[0.18em] text-[#0052CC] font-semibold">AI Diagnostic Summary</div>
                <span className="font-mono text-[10px] text-slate-500">confidence {mainAlert.confidence}%</span>
              </div>
              <div className="mt-1 text-slate-900 font-medium leading-snug">{suggested}</div>
              <div className="mt-1 text-sm text-slate-600">{mainAlert.aiSummary}</div>
            </div>
            <Button className="bg-[#0052CC] hover:bg-[#0043A6]" onClick={handleCreateTicket}>
              Dispatch Service
            </Button>
          </div>
        </Card>
      )}

      {/* Top grid: customer + live snapshot + health score */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="p-5 border-slate-200 shadow-sm" data-testid="customer-info-card">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Customer Info</div>
          <div className="mt-3 space-y-2.5 text-sm">
            <Row label="Name" value={<Link to={`/customers/${customer.id}`} className="text-[#0052CC] hover:underline font-medium">{customer.name}</Link>} />
            <Row label="Address" value={`${customer.address}, ${customer.city}`} />
            <Row label="Phone" value={<span className="font-mono">{customer.phone}</span>} />
            <Row label="Email" value={<span className="font-mono text-xs">{customer.email}</span>} />
            <Separator />
            <Row label="Plan" value={<StatusBadge status={customer.plan === 'Premium Comfort Protection' ? 'warning' : customer.plan === 'Preventive Maintenance' ? 'healthy' : customer.plan === 'Basic Monitoring' ? 'info' : 'critical'}>{customer.plan}</StatusBadge>} />
            <Row label="Preferred Tech" value={tech ? tech.name : '—'} />
            <Row label="Last Service" value={<span className="font-mono text-xs">{customer.lastServiceDate}</span>} />
            <Row label="Next Visit" value={<span className="font-mono text-xs">{customer.nextScheduledService}</span>} />
          </div>
        </Card>

        <Card className="p-5 border-slate-200 shadow-sm" data-testid="live-snapshot-card">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Live System Snapshot</div>
            <span className="font-mono text-[10px] text-slate-500">check-in {device.lastCheckIn}</span>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <Metric icon={Thermometer} label="Indoor" value={device.connection === 'offline' ? '—' : `${device.indoorTemp}°F`} />
            <Metric icon={Thermometer} label="Outdoor" value={`${device.outdoorTemp}°F`} />
            <Metric icon={Droplet} label="Humidity" value={device.connection === 'offline' ? '—' : `${device.humidity}%`} />
            <Metric icon={Gauge} label="Setpoint" value={`${device.setpoint}°F`} />
            <Metric icon={Activity} label="Stage" value={device.stage} small />
            <Metric icon={Wind} label="Fan" value={device.fanStatus} small />
            <Metric icon={Clock} label="Runtime today" value={`${device.runtimeTodayMin} min`} />
            <Metric icon={Clock} label="This week" value={`${device.runtimeWeekHr} hr`} />
          </div>
        </Card>

        <Card className="p-5 border-slate-200 shadow-sm flex flex-col items-center" data-testid="health-score-card">
          <div className="self-start text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">System Health Score</div>
          <div className="mt-2">
            <HealthScoreGauge score={device.healthScore} size={180} />
          </div>
          <div className="mt-3 w-full space-y-1.5">
            {SUB_SCORES.map((s) => {
              const val = subs[s.key];
              const Icon = s.icon;
              const color = val >= 85 ? 'bg-emerald-500' : val >= 70 ? 'bg-blue-500' : val >= 60 ? 'bg-amber-500' : val > 0 ? 'bg-red-500' : 'bg-slate-400';
              return (
                <div key={s.key} className="flex items-center gap-2 text-xs">
                  <Icon className="h-3.5 w-3.5 text-slate-500" />
                  <span className="flex-1 text-slate-700">{s.label}</span>
                  <div className="w-16 h-1 rounded-full bg-slate-100 overflow-hidden">
                    <div className={color + ' h-full'} style={{ width: `${val}%` }} />
                  </div>
                  <span className="font-mono text-slate-700 w-7 text-right">{val}</span>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Charts */}
      <Tabs defaultValue="temp" className="w-full">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Performance Trends</div>
            <div className="mt-0.5 text-lg font-semibold text-slate-900">Telemetry over time</div>
          </div>
          <TabsList>
            <TabsTrigger value="temp" data-testid="chart-tab-temp">Temperature</TabsTrigger>
            <TabsTrigger value="humidity" data-testid="chart-tab-humidity">Humidity</TabsTrigger>
            <TabsTrigger value="runtime" data-testid="chart-tab-runtime">Runtime</TabsTrigger>
            <TabsTrigger value="cycles" data-testid="chart-tab-cycles">Cycles</TabsTrigger>
            <TabsTrigger value="score" data-testid="chart-tab-score">Health</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="temp">
          <Card className="p-5 border-slate-200 shadow-sm mt-3">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={temps}>
                  <defs>
                    <linearGradient id="tempG" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0052CC" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#0052CC" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="t" fontSize={11} stroke="#64748B" tickLine={false} axisLine={false} />
                  <YAxis fontSize={11} stroke="#64748B" tickLine={false} axisLine={false} domain={[60, 90]} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 12 }} />
                  <Area type="monotone" dataKey="indoor" stroke="#0052CC" fill="url(#tempG)" strokeWidth={2} />
                  <Line type="monotone" dataKey="setpoint" stroke="#F59E0B" strokeDasharray="4 3" strokeWidth={1.5} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </TabsContent>
        <TabsContent value="humidity">
          <Card className="p-5 border-slate-200 shadow-sm mt-3">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={hum}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="d" fontSize={11} stroke="#64748B" tickLine={false} axisLine={false} />
                  <YAxis fontSize={11} stroke="#64748B" tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 12 }} />
                  <Line type="monotone" dataKey="humidity" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </TabsContent>
        <TabsContent value="runtime">
          <Card className="p-5 border-slate-200 shadow-sm mt-3">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={runtime}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="d" fontSize={11} stroke="#64748B" tickLine={false} axisLine={false} />
                  <YAxis fontSize={11} stroke="#64748B" tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 12 }} />
                  <Bar dataKey="minutes" fill="#0052CC" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </TabsContent>
        <TabsContent value="cycles">
          <Card className="p-5 border-slate-200 shadow-sm mt-3">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={cycles}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="h" fontSize={11} stroke="#64748B" tickLine={false} axisLine={false} />
                  <YAxis fontSize={11} stroke="#64748B" tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 12 }} />
                  <Bar dataKey="cycles" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </TabsContent>
        <TabsContent value="score">
          <Card className="p-5 border-slate-200 shadow-sm mt-3">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={healthTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="w" fontSize={11} stroke="#64748B" tickLine={false} axisLine={false} />
                  <YAxis fontSize={11} stroke="#64748B" tickLine={false} axisLine={false} domain={[30, 100]} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 12 }} />
                  <Line type="monotone" dataKey="score" stroke="#0052CC" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Diagnostics grid */}
      <div>
        <div className="flex items-end justify-between mb-3">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Advanced Diagnostic Signals</div>
            <div className="mt-0.5 text-lg font-semibold text-slate-900">AI-driven anomaly detection</div>
          </div>
          <span className="text-xs text-slate-500 font-mono">11 signals monitored</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {diagnostics.map((d) => (
            <DiagnosticCard key={d.name} {...d} />
          ))}
        </div>
      </div>

      {/* Alert history */}
      <Card className="p-5 border-slate-200 shadow-sm" data-testid="alert-history-card">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Alert History</div>
            <div className="mt-0.5 text-lg font-semibold text-slate-900">Timeline of diagnostic events</div>
          </div>
          <Button variant="outline" size="sm" onClick={() => toast.success('Exporting full history...')}>
            <History className="h-4 w-4 mr-2" /> Full History
          </Button>
        </div>
        <div className="mt-5 space-y-4">
          {(deviceAlerts.length ? deviceAlerts : []).map((a) => (
            <div key={a.id} className="flex gap-4">
              <div className="shrink-0 flex flex-col items-center">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                  a.severity === 'critical' ? 'bg-red-50 text-red-600' :
                  a.severity === 'warning' ? 'bg-amber-50 text-amber-700' :
                  a.severity === 'watchlist' ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-600'
                }`}>
                  <AlertTriangle className="h-4 w-4" />
                </div>
                <div className="w-px flex-1 bg-slate-200 mt-2" />
              </div>
              <div className="flex-1 pb-4">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-slate-900">{a.type}</div>
                  <StatusBadge status={a.severity} />
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{a.createdAgo} · confidence <span className="font-mono">{a.confidence}%</span></div>
                <div className="mt-1.5 text-sm text-slate-600">{a.aiSummary}</div>
              </div>
            </div>
          ))}
          {!deviceAlerts.length && (
            <div className="text-center py-8 text-slate-500 text-sm">
              <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-500" />
              <div className="mt-2">No recent alerts. System operating normally.</div>
            </div>
          )}
        </div>
      </Card>

      <div className="text-center pt-2">
        <Button variant="outline" onClick={() => toast.success('Opening full device report...')} data-testid="device-view-full-history">
          <FileText className="h-4 w-4 mr-2" /> View Full Device Report
        </Button>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs uppercase tracking-wider text-slate-500">{label}</span>
      <span className="text-sm text-slate-800 text-right">{value}</span>
    </div>
  );
}

function Metric({ icon: Icon, label, value, small }) {
  return (
    <div className="rounded-md border border-slate-200 p-3">
      <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className={`mt-1 font-mono font-semibold text-slate-900 ${small ? 'text-sm' : 'text-xl'}`}>{value}</div>
    </div>
  );
}

function DiagnosticCard({ name, status, confidence, explanation, action }) {
  const bg = {
    normal: 'bg-emerald-50/60 border-emerald-200',
    watch: 'bg-blue-50/60 border-blue-200',
    warning: 'bg-amber-50/60 border-amber-200',
    critical: 'bg-red-50/60 border-red-200',
  }[status];
  return (
    <div className={`rounded-lg border p-4 ${bg}`} data-testid={`diag-${name.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-slate-900">{name}</div>
        <StatusBadge status={status} />
      </div>
      <div className="mt-1 text-[11px] font-mono text-slate-500">confidence {confidence}%</div>
      <div className="mt-2 text-xs text-slate-700 leading-relaxed">{explanation}</div>
      <div className="mt-2 text-xs text-slate-600 border-t border-slate-200/70 pt-2">
        <span className="font-semibold text-slate-800">Action:</span> {action}
      </div>
    </div>
  );
}
